import { useParams } from 'react-router-dom';
import TextWinds from '../../../components/common/TextWinds';
import { useAppDispatch, useAppSelector } from '../../../store/hook';
import { selectLmsLists } from '../../../store/features/lmsSlice';
import { useEffect, useState } from 'react';
import {
  ClassLessonItem,
  ClassLessonSubjectItem,
} from '../../../constants/types/lms/take-class/type';
import {
  getClassLessonList,
  postContentView,
} from '../../../api/utils/lms/take-class/api';
import { openAlert } from '../../../store/features/modalSlice';
import { getErrorMessages } from '../../../api/utils/util-func';
import { openContentsView } from '../../../utils/openContentsWindow';
import { LoadingSpinner } from '../../Loading/LoadingSpinner';
import ClassLessonList from './ClassLessonList';
import { Button } from 'antd';

interface BookLessonPanelProps {
  selectedBookCode: string;
  onBack: () => void;
}

/**
 * 개편 LMS (교사) - 수업하기 페이지 > 북별 레슨 판넬
 */
const BookLessonPanel = ({
  selectedBookCode,
  onBack,
}: BookLessonPanelProps) => {
  const { classId } = useParams();

  const lmsLists = useAppSelector(selectLmsLists);

  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [lessonList, setLessonList] = useState<ClassLessonItem[]>([]);

  const fetchClassLessonList = async () => {
    try {
      setIsLoading(true);

      const response = await getClassLessonList({
        bookCode: selectedBookCode,
        classId,
      });

      setLessonList(response.lessons ?? []);
    } catch (error) {
      dispatch(openAlert({ message: getErrorMessages(error) }));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!lmsLists.course || !classId) return;

    fetchClassLessonList();
  }, [lmsLists.course, classId, selectedBookCode, dispatch]);

  const handlePptClick = async (
    lesson: ClassLessonItem,
    subject: ClassLessonSubjectItem,
  ) => {
    try {
      if (!subject.pptContentId) {
        dispatch(openAlert({ message: 'PPT 콘텐츠의 id 가 없습니다.' }));
        return;
      }

      openContentsView(classId, subject.pptContentId);

      await postContentView({
        bookCode: selectedBookCode,
        lesson: lesson.lesson,
        subjectCode: subject.subjectCode,
        contentType: 'PPT',
      });

      await fetchClassLessonList();
    } catch (error) {
      dispatch(openAlert({ message: getErrorMessages(error) }));
    }
  };

  const handleVideoClick = async (
    lesson: ClassLessonItem,
    subject: ClassLessonSubjectItem,
  ) => {
    try {
      if (!subject.videoContentId) {
        dispatch(openAlert({ message: 'VIDEO 콘텐츠의 id 가 없습니다.' }));
        return;
      }

      openContentsView(classId, subject.videoContentId);

      await postContentView({
        bookCode: selectedBookCode,
        lesson: lesson.lesson,
        subjectCode: subject.subjectCode,
        contentType: 'VIDEO',
      });

      await fetchClassLessonList();
    } catch (error) {
      dispatch(openAlert({ message: getErrorMessages(error) }));
    }
  };

  const handleSelfClick = async (
    lesson: ClassLessonItem,
    subject: ClassLessonSubjectItem,
  ) => {
    try {
      if (!subject.selfContentId) {
        dispatch(openAlert({ message: 'SELF 콘텐츠의 id 가 없습니다.' }));
        return;
      }

      openContentsView(classId, subject.selfContentId);

      await postContentView({
        bookCode: selectedBookCode,
        lesson: lesson.lesson,
        subjectCode: subject.subjectCode,
        contentType: 'SELF',
      });

      await fetchClassLessonList();
    } catch (error) {
      dispatch(openAlert({ message: getErrorMessages(error) }));
    }
  };

  const handleReadingOnClick = async (
    lesson: ClassLessonItem,
    subject: ClassLessonSubjectItem,
  ) => {
    try {
      if (!subject.readingOnContentId) {
        dispatch(openAlert({ message: 'READING_ON 콘텐츠의 id 가 없습니다.' }));
        return;
      }

      openContentsView(classId, subject.readingOnContentId);

      await postContentView({
        bookCode: selectedBookCode,
        lesson: lesson.lesson,
        subjectCode: subject.subjectCode,
        contentType: 'READING_ON',
      });

      await fetchClassLessonList();
    } catch (error) {
      dispatch(openAlert({ message: getErrorMessages(error) }));
    }
  };

  if (isLoading) {
    return <LoadingSpinner message="데이터 조회중" />;
  }

  return (
    <section className="rounded-xl bg-white px-3 pt-6 shadow-card xl:px-8">
      <div className="mb-5 flex items-center gap-3">
        <Button
          type="default"
          className="flex items-center gap-2 rounded-lg px-5"
          onClick={onBack}
        >
          ← 교재 목록으로
        </Button>

        <div className="min-w-0">
          <TextWinds type="title_h3" className="truncate">
            {selectedBookCode}
          </TextWinds>
        </div>
      </div>

      <ClassLessonList
        bookCode={selectedBookCode}
        lessons={lessonList}
        onPptClick={handlePptClick}
        onVideoClick={handleVideoClick}
        onSelfClick={handleSelfClick}
        onReadingOnClick={handleReadingOnClick}
      />
    </section>
  );
};

export default BookLessonPanel;
